import { forwardRef } from "react";
import WarningIcon from "components/Icons/SystemMessaging/WarningIcon";
import ErrorIcon from "components/Icons/SystemMessaging/ErrorIcon";
import InfoIcon from "components/Icons/SystemMessaging/InfoIcon";
import { styled } from "@mui/material";
import Typography from "@mui/material/Typography";
import type { AlertProps } from "@mui/material/Alert";
import MuiAlert from "@mui/material/Alert";
import SuccessIcon from "components/Icons/SystemMessaging/SuccessIcon";

const iconMapping = {
  warning: <WarningIcon fontSize="inherit" />,
  error: <ErrorIcon fontSize="inherit" />,
  info: <InfoIcon fontSize="inherit" />,
  success: <SuccessIcon sx={{ fontSize: "inherit" }} />,
};

const StyledSnackAlert = styled(MuiAlert)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderRadius: "4px",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5, 2),
  display: "flex",
  alignItems: "center",

  "& .MuiAlert-icon": {
    padding: "0",
    marginRight: "4px",
  },

  "& .MuiAlert-action": {
    padding: "0 0 0 16px",
    color: theme.palette.grey[120],
  },

  "& .MuiAlert-message": {
    padding: "0",
    fontSize: theme.typography.TextM.fontSize,
    lineHeight: theme.typography.TextM.lineHeight,
    fontWeight: "500",
  },

  "&.MuiAlert-colorSuccess": {
    backgroundColor: theme.palette.success.light,
  },

  "&.MuiAlert-colorWarning": {
    backgroundColor: theme.palette.warning.light,
  },

  "&.MuiAlert-colorError": {
    backgroundColor: theme.palette.error.light,
  },
}));

interface SnackAlertProps extends AlertProps {
  description?: string;
}

const SnackAlert = forwardRef<HTMLDivElement, SnackAlertProps>(({ description, children, ...props }, ref) => {
  return (
    <StyledSnackAlert ref={ref} iconMapping={iconMapping} {...props}>
      {children}
      {description && (
        <Typography variant="body2" sx={{ fontWeight: 400, color: "grey.120" }}>
          {description}
        </Typography>
      )}
    </StyledSnackAlert>
  );
});

export default SnackAlert;
